import type { SemanticSection, TrackSampler, Vec3 } from '../track/index.ts';

export type Direction = -1 | 1;
export type ValidationSeverity = 'info' | 'warning' | 'severe' | 'block';

export interface TrackBoundState {
  readonly sRef: number;
  readonly velocity: number;
  readonly direction: Direction;
  readonly time: number;
  readonly sectionRuntime: Readonly<Record<string, unknown>>;
}

export interface TrainDefinition {
  readonly carCount: number;
  readonly carMass: number;
  readonly carSpacing: number;
  readonly bogieSpacing: number;
  readonly rollingResistanceCoefficient: number;
  readonly dragCoefficient: number;
  readonly dragArea: number;
}

export interface FamilyLimits {
  readonly maxSpeed?: number;
  readonly maxCars?: number;
}

export interface CoasterFamilyDefinition {
  readonly id: string;
  readonly train: TrainDefinition;
  readonly capabilities: ReadonlySet<string>;
  readonly limits: FamilyLimits;
}

export interface SimulationConfig {
  readonly sampler: TrackSampler;
  readonly family: CoasterFamilyDefinition;
  readonly gravity?: Vec3;
  readonly airDensity?: number;
}

export interface CarPose {
  readonly carIndex: number;
  readonly distance: number;
  readonly position: Vec3;
  readonly forward: Vec3;
  readonly up: Vec3;
  readonly right: Vec3;
  readonly frontBogieDistance: number;
  readonly rearBogieDistance: number;
}

export interface GVector {
  readonly vertical: number;
  readonly lateral: number;
  readonly longitudinal: number;
}

export interface ForceSample extends GVector {
  readonly carIndex: number;
  readonly distance: number;
  readonly direction: Direction;
  readonly speed: number;
  readonly durationMs: number;
  readonly severity: ValidationSeverity;
  readonly windows: Readonly<Record<string, GVector>>;
}

export interface ValidationIssue {
  readonly code: string;
  readonly severity: ValidationSeverity;
  readonly messageKey: string;
  readonly sectionId?: string;
  readonly distance?: number;
  readonly carIndex?: number;
  readonly measured?: number;
  readonly threshold?: number;
}

export interface SimulationFrame {
  readonly time: number;
  readonly referenceState: TrackBoundState;
  readonly cars: readonly CarPose[];
  readonly forces: readonly ForceSample[];
  readonly issues: readonly ValidationIssue[];
}

export interface DynamicsBackend<State> {
  readonly motionModel: string;
  step(state: State, config: SimulationConfig, dt: number): SimulationFrame;
}

export interface SectionForceResult {
  readonly acceleration: number;
  readonly section: SemanticSection | null;
}

export const FORCE_WINDOWS_MS = [50, 100, 200, 500, 1000, 2000] as const;
export const FIXED_PHYSICS_STEP = 1 / 120;
export const STANDARD_GRAVITY = 9.80665;
